import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import QRCode from "react-qr-code";
import { CalendarBlank, Clock, MapPin, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import { PageShell } from "@/components/Layout";

export default function TicketPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/tickets/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Ticket not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <PageShell>
        <div className="p-16 text-[#A3A3A3] font-mono-overline">Loading…</div>
      </PageShell>
    );
  }
  if (!data || !data.ticket) {
    return (
      <PageShell>
        <div className="max-w-xl mx-auto p-16 text-center">
          <h1 className="text-3xl font-serif-display text-[#EDEDED] mb-4">Ticket not found</h1>
          <Link to="/social-events" className="font-mono-overline text-[#e2a9f1]">← Back to events</Link>
        </div>
      </PageShell>
    );
  }

  const { ticket, event } = data;
  const dt = event ? (typeof event.date_time === "string" ? parseISO(event.date_time) : new Date(event.date_time)) : null;

  return (
    <PageShell>
      <section className="px-6 lg:px-12 py-20" data-testid="ticket-page">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 font-mono-overline text-[#e2a9f1] mb-4">
              <Check size={16} weight="bold" /> Reservation Confirmed
            </div>
            <h1 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED]" data-testid="ticket-confirm-heading">
              Your evening awaits.
            </h1>
            <p className="text-[#A3A3A3] mt-4">
              A copy has been sent to <span className="text-[#EDEDED]">{ticket.email}</span>.
            </p>
          </motion.div>

          {/* The digital ticket — wallet-pass style */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-[#111111] border border-[#e2a9f1]/40 relative overflow-hidden"
            data-testid="digital-ticket"
          >
            {/* Top */}
            <div className="p-8 md:p-10 border-b border-dashed border-[#2A2A2A] relative">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="font-mono-overline text-[#e2a9f1]">Swingle Events</div>
                  <div className="text-2xl font-brand text-[#EDEDED] mt-1">Digital Pass</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-overline text-[#A3A3A3]">Quantity</div>
                  <div className="text-3xl font-serif-display text-[#e2a9f1]">×{ticket.quantity}</div>
                </div>
              </div>
              {event && (
                <>
                  <h2 className="text-3xl md:text-4xl font-serif-display text-[#EDEDED] leading-tight mb-6" data-testid="ticket-event-title">
                    {event.title}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-sm">
                    <div>
                      <CalendarBlank size={16} weight="thin" className="text-[#e2a9f1] mb-2" />
                      <div className="font-mono-overline text-[#A3A3A3]">Date</div>
                      <div className="text-[#EDEDED]">{format(dt, "EEE d MMM")}</div>
                    </div>
                    <div>
                      <Clock size={16} weight="thin" className="text-[#e2a9f1] mb-2" />
                      <div className="font-mono-overline text-[#A3A3A3]">Doors</div>
                      <div className="text-[#EDEDED]">{format(dt, "h:mm a")}</div>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <MapPin size={16} weight="thin" className="text-[#e2a9f1] mb-2" />
                      <div className="font-mono-overline text-[#A3A3A3]">Venue</div>
                      <div className="text-[#EDEDED]">{event.venue}, {event.city}</div>
                    </div>
                  </div>
                </>
              )}
              {/* Punch holes for ticket vibe */}
              <div className="absolute -left-3 top-1/2 w-6 h-6 rounded-full bg-[#0A0A0A]" />
              <div className="absolute -right-3 top-1/2 w-6 h-6 rounded-full bg-[#0A0A0A]" />
            </div>

            {/* Bottom (QR) */}
            <div className="p-8 md:p-10 flex flex-col md:flex-row items-center gap-10">
              <div className="bg-white p-4 rounded-sm" data-testid="ticket-qr">
                <QRCode value={`SWINGLE-TICKET:${ticket.id}`} size={160} bgColor="#FFFFFF" fgColor="#0A0A0A" />
              </div>
              <div className="flex-1 w-full">
                <div className="font-mono-overline text-[#A3A3A3] mb-2">Guest</div>
                <div className="text-xl text-[#EDEDED] mb-5">{ticket.full_name}</div>
                <div className="grid grid-cols-2 gap-5 mb-5">
                  <div>
                    <div className="font-mono-overline text-[#A3A3A3]">Type</div>
                    <div className="text-[#EDEDED]">{ticket.attendee_type === "couple" ? "Couple" : "Single Lady"}</div>
                  </div>
                  <div>
                    <div className="font-mono-overline text-[#A3A3A3]">Paid</div>
                    <div className="text-[#e2a9f1] font-serif-display text-xl">£{Number(ticket.amount_paid).toFixed(2)}</div>
                  </div>
                </div>
                <div className="font-mono-overline text-[#A3A3A3]">Ticket ID</div>
                <div className="text-xs text-[#EDEDED] break-all tracking-wider" data-testid="ticket-id">{ticket.id}</div>
              </div>
            </div>
          </motion.div>

          <div className="mt-10 text-center text-sm text-[#A3A3A3] leading-relaxed">
            Save this page or screenshot the QR. Present at the door — discretion observed at all
            times. Doors close 45 minutes after start.
          </div>
          <div className="text-center mt-8">
            <Link to="/social-events" data-testid="back-to-events" className="font-mono-overline text-[#e2a9f1] hover:text-[#E6C27A]">
              ← Browse other evenings
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
